import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { api, useAuth } from "../App";
import {
  Brain,
  Cloud,
  GitBranch,
  Shield,
  Code,
  Smartphone,
  BarChart,
  Link,
  Settings,
  Layout,
  Palette,
  Rocket,
  Users,
  TrendingUp,
  Github,
  Terminal,
  ArrowRight,
  ArrowLeft,
  Check,
  List,
  Flame,
  BookOpen,
  Lightbulb,
  FlaskConical,
} from "lucide-react";

const SENIORITY_OPTIONS = [
  { value: "software-engineer", label: "Software Engineer" },
  { value: "senior-engineer", label: "Senior Engineer" },
  { value: "staff-engineer", label: "Staff / Principal Engineer" },
  { value: "tech-lead", label: "Tech Lead" },
  { value: "engineering-manager", label: "Engineering Manager" },
  { value: "director", label: "Director" },
  { value: "vp", label: "VP of Engineering" },
  { value: "cto", label: "CTO" },
];

const POST_STYLE_OPTIONS = [
  {
    value: "technical",
    label: "Technical Depth",
    description: "Code examples, architectural breakdowns, framework deep-dives",
    audience: "For Senior Engineers",
    icon: Code,
  },
  {
    value: "story",
    label: "Personal Story",
    description: "Relatable narratives, lessons learned from real experience",
    audience: "For Broad Tech Audience",
    icon: BookOpen,
  },
  {
    value: "leadership",
    label: "Leadership Angle",
    description: "Strategic insights, team dynamics, org-level decisions",
    audience: "For Managers & Executives",
    icon: TrendingUp,
  },
  {
    value: "listicle",
    label: "Listicle / Tips",
    description: "Numbered takeaways, quick wins, actionable checklists",
    audience: "High engagement, broad reach",
    icon: List,
  },
  {
    value: "contrarian",
    label: "Contrarian Take",
    description: "Hot takes, challenging conventional wisdom, debate-sparking",
    audience: "For Bold Thinkers",
    icon: Flame,
  },
  {
    value: "case-study",
    label: "Case Study",
    description: "Problem → approach → measurable results with real data",
    audience: "For Practitioners & PMs",
    icon: FlaskConical,
  },
  {
    value: "insight",
    label: "Industry Insight",
    description: "Trend analysis, market observations, forward-looking takes",
    audience: "For Thought Leaders",
    icon: Lightbulb,
  },
];

const TECH_KEYWORD_OPTIONS = [
  "Python", "JavaScript", "TypeScript", "Go", "Rust", "Java", "C++", "Swift", "Kotlin", "SQL",
  "React", "Vue", "Angular", "Next.js", "Tailwind CSS",
  "Node.js", "Django", "FastAPI", "Spring Boot", "Rails",
  "AWS", "Azure", "GCP", "Serverless",
  "LLMs", "PyTorch", "TensorFlow", "LangChain", "OpenAI API",
  "Docker", "Kubernetes", "Terraform", "CI/CD", "GitHub Actions",
  "PostgreSQL", "MongoDB", "Redis", "DynamoDB",
  "GraphQL", "REST APIs", "Microservices", "System Design",
];

const TOPIC_ICONS = {
  "ai-ml": Brain,
  "cloud": Cloud,
  "devops": GitBranch,
  "security": Shield,
  "web-dev": Code,
  "mobile": Smartphone,
  "data-science": BarChart,
  "blockchain": Link,
  "engineering": Settings,
  "product": Layout,
  "ux-design": Palette,
  "startup": Rocket,
  "leadership": Users,
  "career": TrendingUp,
  "open-source": Github,
  "programming": Terminal,
};

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [step, setStep] = useState(1);

  // Step 1: Topics
  const [topics, setTopics] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Step 2: Self-profile
  const [seniority, setSeniority] = useState("");
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [preferredStyles, setPreferredStyles] = useState([]);

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const response = await api.get("/topics");
        setTopics(response.data.topics);
      } catch (error) {
        console.error("Failed to fetch topics:", error);
        toast.error("Failed to load topics. Please refresh the page.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchTopics();
  }, []);

  const toggleTopic = (topicId) => {
    setSelectedTopics((prev) => {
      if (prev.includes(topicId)) return prev.filter((t) => t !== topicId);
      if (prev.length >= 7) { toast.error("You can select up to 7 topics"); return prev; }
      return [...prev, topicId];
    });
  };

  const toggleKeyword = (kw) => {
    setSelectedKeywords((prev) =>
      prev.includes(kw) ? prev.filter((k) => k !== kw) : [...prev, kw]
    );
  };

  const handleNextStep = () => {
    if (selectedTopics.length < 1) {
      toast.error("Please select at least 1 topic");
      return;
    }
    setStep(2);
  };

  const toggleStyle = (value) => {
    setPreferredStyles((prev) => {
      if (prev.includes(value)) return prev.filter((s) => s !== value);
      if (prev.length >= 3) { toast.error("You can select up to 3 styles"); return prev; }
      return [...prev, value];
    });
  };

  const handleFinish = async () => {
    setIsSaving(true);
    try {
      await api.post("/user/onboarding", {
        topics: selectedTopics,
        seniority: seniority || null,
        tech_keywords: selectedKeywords,
        preferred_post_style: preferredStyles,
      });

      setUser({ ...user, has_completed_onboarding: true });
      toast.success("Setup complete!");

      // try { await api.post("/seed"); } catch { /* ignore */ }

      navigate("/feed", { replace: true });
    } catch (error) {
      console.error("Failed to complete onboarding:", error);
      toast.error("Failed to save settings. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const getIcon = (iconName) => {
    const IconComponent = TOPIC_ICONS[iconName] || Code;
    return <IconComponent className="w-6 h-6" />;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="PostWriter" className="w-8 h-8" />
              <span className="font-semibold text-lg tracking-tight">PostWriter</span>
            </div>
            <div className="flex items-center gap-3">
              {/* Step indicator */}
              <div className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${step >= 1 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>1</div>
                <div className={`w-12 h-0.5 ${step >= 2 ? "bg-primary" : "bg-muted"}`} />
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${step >= 2 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>2</div>
              </div>
              {user?.picture && (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* ── STEP 1: Topics ── */}
        {step === 1 && (
          <>
            <div className="text-center mb-12">
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4">
                What topics interest you?
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Select 1–7 topics to personalise your feed. You can change these anytime in settings.
              </p>
            </div>

            <div className="flex items-center justify-center mb-8">
              <div className="px-4 py-2 bg-muted rounded-full">
                <span className="font-medium text-foreground">{selectedTopics.length}</span>
                <span className="text-muted-foreground"> / 7 selected</span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
              {topics.map((topic) => {
                const isSelected = selectedTopics.includes(topic.id);
                return (
                  <button
                    key={topic.id}
                    onClick={() => toggleTopic(topic.id)}
                    data-testid={`topic-${topic.id}`}
                    className={`relative p-5 rounded-xl border-2 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${isSelected ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary/40"}`}
                  >
                    {isSelected && (
                      <div className="absolute top-3 right-3">
                        <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                          <Check className="w-4 h-4 text-primary-foreground" />
                        </div>
                      </div>
                    )}
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${isSelected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                      {getIcon(topic.id)}
                    </div>
                    <h3 className="font-semibold text-foreground mb-1">{topic.name}</h3>
                    <p className="text-sm text-muted-foreground">{topic.description}</p>
                  </button>
                );
              })}
            </div>

            <div className="flex justify-center">
              <Button
                size="lg"
                onClick={handleNextStep}
                disabled={selectedTopics.length < 1}
                data-testid="continue-btn"
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-lg rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next: Your Profile
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </>
        )}

        {/* ── STEP 2: Professional Profile ── */}
        {step === 2 && (
          <>
            <div className="text-center mb-12">
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4">
                Tell us about yourself
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                This helps us personalise your post content and style. All fields are optional.
              </p>
            </div>

            <div className="max-w-3xl mx-auto space-y-10">

              {/* Seniority */}
              <section>
                <h2 className="text-lg font-semibold text-foreground mb-4">Your Role</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {SENIORITY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setSeniority(seniority === opt.value ? "" : opt.value)}
                      className={`p-3 rounded-xl border-2 text-left text-sm transition-all duration-200 ${seniority === opt.value ? "border-primary bg-primary/10 text-primary font-medium" : "border-border bg-card hover:border-primary/40 text-foreground"}`}
                    >
                      {seniority === opt.value && <Check className="w-3.5 h-3.5 inline mr-1.5 text-primary" />}
                      {opt.label}
                    </button>
                  ))}
                </div>
              </section>

              {/* Tech Keywords */}
              <section>
                <h2 className="text-lg font-semibold text-foreground mb-1">Your Tech Stack</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Select the technologies you work with. These are woven into your generated posts.
                </p>
                <div className="flex flex-wrap gap-2">
                  {TECH_KEYWORD_OPTIONS.map((kw) => {
                    const isSelected = selectedKeywords.includes(kw);
                    return (
                      <button
                        key={kw}
                        onClick={() => toggleKeyword(kw)}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-all duration-150 ${isSelected ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground hover:border-primary/50"}`}
                      >
                        {kw}
                      </button>
                    );
                  })}
                </div>
                {selectedKeywords.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-2">{selectedKeywords.length} selected</p>
                )}
              </section>

              {/* Post Style */}
              <section>
                <div className="flex items-center justify-between mb-1">
                  <h2 className="text-lg font-semibold text-foreground">Preferred Post Style</h2>
                  <span className="text-sm text-muted-foreground">
                    <span className={preferredStyles.length === 3 ? "text-primary font-medium" : ""}>{preferredStyles.length}</span> / 3 selected
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Pick up to 3 styles that resonate with you.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {POST_STYLE_OPTIONS.map((opt) => {
                    const Icon = opt.icon;
                    const isSelected = preferredStyles.includes(opt.value);
                    const isDisabled = !isSelected && preferredStyles.length >= 3;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => toggleStyle(opt.value)}
                        disabled={isDisabled}
                        className={`relative p-4 rounded-xl border-2 text-left transition-all duration-200 ${isDisabled ? "opacity-40 cursor-not-allowed" : "hover:-translate-y-0.5 hover:shadow-md"} ${isSelected ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary/40"}`}
                      >
                        {isSelected && (
                          <div className="absolute top-3 right-3 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-primary-foreground" />
                          </div>
                        )}
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2.5 ${isSelected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <p className="font-semibold text-foreground text-sm mb-1">{opt.label}</p>
                        <p className="text-xs text-muted-foreground mb-1.5 leading-relaxed">{opt.description}</p>
                        <span className="text-xs text-primary font-medium">{opt.audience}</span>
                      </button>
                    );
                  })}
                </div>
              </section>

            </div>

            {/* Navigation */}
            <div className="flex justify-between mt-12 max-w-3xl mx-auto">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              <Button
                size="lg"
                onClick={handleFinish}
                disabled={isSaving}
                data-testid="continue-btn"
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? "Saving..." : (
                  <>
                    Finish Setup
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </>
        )}

      </main>
    </div>
  );
};

export default OnboardingPage;
